// @ts-check
import axios from 'axios'

const BASE = '/api/personal-analyst/jobs'

/** @typedef {import('./schema').components['schemas']} Schemas */
/** @typedef {import('./schema').operations} Operations */
/** @typedef {Schemas['AnalystJob']} AnalystJob */
/** @typedef {Schemas['PaginatedAnalystJobList']} PaginatedAnalystJobList */
/** @typedef {Operations['api_personal_analyst_jobs_list']['parameters']['query']} ListJobsParams */
/** @typedef {Operations['api_personal_analyst_jobs_bulk_sync_create']['parameters']['query']} BulkSyncParams */
/** @typedef {Operations['api_personal_analyst_jobs_care_gap_summary_retrieve']['parameters']['query']} CareGapSummaryParams */
/**
 * The server fills `query` for templated job types, so every field is optional here.
 * @typedef {Partial<Pick<AnalystJob, 'query' | 'job_type' | 'entity' | 'include_descendants'>>} CreateJobPayload
 */

/**
 * GET /api/personal-analyst/jobs/ -- one page of jobs, newest first. Always pass `limit`.
 * @param {ListJobsParams & { limit: number }} params
 * @returns {Promise<PaginatedAnalystJobList>}
 */
export async function listJobs(params) {
  const { job_type__in: jobTypes, ...rest } = params
  // The API takes job_type__in comma-separated (explode: false); axios would send job_type__in[]=...
  const query = jobTypes ? { ...rest, job_type__in: jobTypes.join(',') } : rest
  const { data } = await axios.get(`${BASE}/`, { params: query })
  return data
}

/**
 * GET /api/personal-analyst/jobs/{id}/ -- one job with its feedback; lazy-syncs a pending result.
 * @param {number} id
 * @returns {Promise<AnalystJob>}
 */
export async function getJob(id) {
  const { data } = await axios.get(`${BASE}/${id}/`)
  return data
}

/**
 * POST /api/personal-analyst/jobs/ -- queue a new job. Rejects with 429 if one was queued in the last minute.
 * @param {CreateJobPayload} payload
 * @returns {Promise<AnalystJob>}
 */
export async function createJob(payload) {
  const { data } = await axios.post(`${BASE}/`, payload)
  return data
}

/**
 * POST /api/personal-analyst/jobs/bulk-sync/ -- sync the caller's pending jobs; returns only the rows that changed.
 * @param {BulkSyncParams} [params]
 * @returns {Promise<AnalystJob[]>}
 */
export async function bulkSyncJobs(params) {
  const { data } = await axios.post(`${BASE}/bulk-sync/`, null, { params })
  return data
}

/**
 * GET /api/personal-analyst/jobs/{id}/export-pdf/ -- the job's report as a PDF.
 * @param {number} id
 * @returns {Promise<Blob>}
 */
export async function exportJobPdf(id) {
  const { data } = await axios.get(`${BASE}/${id}/export-pdf/`, { responseType: 'blob' })
  return data
}

/**
 * GET /api/personal-analyst/jobs/care-gap-summary/ -- care gap summary history for a practice.
 * @param {CareGapSummaryParams} params
 * @returns {Promise<{ template: number | null, template_name: string, summaries: object[] }>}
 */
export async function getCareGapSummary(params) {
  const { data } = await axios.get(`${BASE}/care-gap-summary/`, { params })
  return data
}
